import { QuestionCircleOutlined } from '@ant-design/icons';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Flex } from 'antd';
import { useState } from 'react';

import { $SpecialGradeLevel } from '@suntex.fim/enums';
import { useFramework } from '@suntex.fim/framework';
import { Button, Modal, Select, Tooltip, useTheme } from '@suntex.fim/ui';

import { usePlayer } from '../hooks';

export function ChangeGradeButton() {
  const { playerInfo, playerId } = usePlayer();
  const { api } = useFramework();
  const { colors } = useTheme();
  const queryClient = useQueryClient();

  const [isGradeModalOpen, setIsGradeModalOpen] = useState(false);
  const [selectedGrade, setSelectedGrade] = useState<number>(
    playerInfo?.player.grade ?? 0,
  );

  const { mutate: assignGrade, isPending: isAssigning } = useMutation({
    mutationFn: async (grade: number) => {
      await api.post_Player_AssignGrade({
        playerId,
        grade: $SpecialGradeLevel.from(grade),
      });
    },
    onSuccess: () => {
      setIsGradeModalOpen(false);
      void queryClient.invalidateQueries({ queryKey: ['player', 'info'] });
      void queryClient.invalidateQueries({
        queryKey: ['player', 'progressSummary'],
      });
    },
  });

  if (!playerInfo) return null;

  return (
    <>
      <Flex align="center" gap={8} style={{ marginTop: 8 }}>
        <Button
          size="small"
          onClick={() => {
            setSelectedGrade(playerInfo.player.grade);
            setIsGradeModalOpen(true);
          }}
        >
          Change my grade
        </Button>
        <Tooltip
          title={`Your grade is ${formatGrade(playerInfo.player.grade)}. You can change this by clicking the "Change my grade" button.`}
        >
          <QuestionCircleOutlined style={{ color: colors.primary }} />
        </Tooltip>
      </Flex>
      <Modal
        title="Change My Grade"
        open={isGradeModalOpen}
        onOk={() => assignGrade(selectedGrade)}
        onCancel={() => setIsGradeModalOpen(false)}
        confirmLoading={isAssigning}
        okText="Save"
      >
        <Select.One
          value={selectedGrade}
          onChange={setSelectedGrade}
          options={GRADE_OPTIONS}
          style={{ width: '100%' }}
        />
      </Modal>
    </>
  );
}

const GRADE_OPTIONS = [
  { value: 0, label: 'Kindergarten' },
  { value: 1, label: 'Grade 1' },
  { value: 2, label: 'Grade 2' },
  { value: 3, label: 'Grade 3' },
  { value: 4, label: 'Grade 4' },
  { value: 5, label: 'Grade 5' },
  { value: 6, label: 'Grade 6' },
  { value: 7, label: 'Grade 7' },
  { value: 8, label: 'Grade 8' },
];

function formatGrade(grade: number): string {
  if (grade === 0) return 'Kindergarten';
  return `Grade ${grade}`;
}
